import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle, Mail, MessageSquare, AlertCircle, Eye, EyeOff, Check, X, ChevronDown, Star, TrendingUp, Shield, Zap, Sparkles, Users } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { OTPVerification } from '../components/auth/OTPVerification';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { validatePassword, isEmailExists, isPhoneExists, validateEmail, hashPassword } from '../utils/security';
import { SEO } from '../components/SEO';
type CountryCode = 'eg' | 'ae' | 'sa';
interface Country {
  code: CountryCode;
  name: string;
  nameAr: string;
  flag: string;
  dialCode: string;
  placeholder: string;
  webhookUrl: string;
}
const countries: Country[] = [{
  code: 'eg',
  name: 'Egypt',
  nameAr: 'مصر',
  flag: '🇪🇬',
  dialCode: '+20',
  placeholder: '1xxxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-eg'
}, {
  code: 'ae',
  name: 'UAE',
  nameAr: 'الإمارات',
  flag: '🇦🇪',
  dialCode: '+971',
  placeholder: '5xxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'sa',
  name: 'Saudi Arabia',
  nameAr: 'السعودية',
  flag: '🇸🇦',
  dialCode: '+966',
  placeholder: '5xxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae' // Using same webhook as UAE
}];
export function RegisterPage() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    brandName: '',
    email: '',
    phone: '',
    password: ''
  });
  const [selectedCountry, setSelectedCountry] = useState<Country>(countries[0]);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const countryRef = useRef<HTMLDivElement>(null);
  const [otpMethod, setOtpMethod] = useState<'whatsapp' | 'email'>('whatsapp');
  const [generatedOTP, setGeneratedOTP] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    phone?: string;
    password?: string[];
    general?: string;
  }>({});
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong'>('weak');
  const {
    register
  } = useAuth();
  const {
    language,
    t
  } = useLanguage();
  const navigate = useNavigate();
  const isRTL = language === 'ar';
  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryRef.current && !countryRef.current.contains(event.target as Node)) {
        setIsCountryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Validate password on change
  useEffect(() => {
    if (formData.password) {
      const validation = validatePassword(formData.password, isRTL);
      setPasswordStrength(validation.strength);
      if (!validation.isValid) {
        setErrors(prev => ({
          ...prev,
          password: validation.errors
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          password: undefined
        }));
      }
    }
  }, [formData.password, isRTL]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear specific error when user types
    if (name === 'email') {
      setErrors(prev => ({
        ...prev,
        email: undefined
      }));
    }
    if (name === 'phone') {
      setErrors(prev => ({
        ...prev,
        phone: undefined
      }));
    }
  };
  const getFullPhoneNumber = () => {
    const phoneWithoutLeadingZero = formData.phone.startsWith('0') ? formData.phone.slice(1) : formData.phone;
    return `${selectedCountry.dialCode}${phoneWithoutLeadingZero}`;
  };
  const validatePhoneForCountry = (phone: string, country: CountryCode): boolean => {
    const cleanPhone = phone.replace(/[\s-]/g, '').replace(/^0/, '');
    if (country === 'eg') {
      // Egyptian: 10 digits starting with 1
      return /^1[0125][0-9]{8}$/.test(cleanPhone);
    } else if (country === 'ae') {
      // UAE: 9 digits starting with 5
      return /^5[0-9]{8}$/.test(cleanPhone);
    } else if (country === 'sa') {
      // Saudi: 9 digits starting with 5
      return /^5[0-9]{8}$/.test(cleanPhone);
    }
    return false;
  };
  const getPhoneErrorMessage = (country: CountryCode): string => {
    if (country === 'eg') {
      return t('error.phoneInvalidEg');
    } else if (country === 'ae') {
      return t('error.phoneInvalidAe');
    } else if (country === 'sa') {
      return isRTL ? 'رقم الهاتف غير صالح (يجب أن يبدأ بـ 05 ويكون 10 أرقام)' : 'Invalid phone number (must start with 05 and be 10 digits)';
    }
    return t('error.phoneInvalidEg');
  };
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    // Validate email format
    if (!validateEmail(formData.email)) {
      newErrors.email = t('error.emailInvalid');
    }
    // Check if email exists
    else if (isEmailExists(formData.email)) {
      newErrors.email = t('error.emailExists');
    }
    // Validate phone format for selected country
    if (!validatePhoneForCountry(formData.phone, selectedCountry.code)) {
      newErrors.phone = getPhoneErrorMessage(selectedCountry.code);
    }
    // Check if phone exists
    else if (isPhoneExists(getFullPhoneNumber())) {
      newErrors.phone = t('error.phoneExists');
    }
    // Validate password
    const passwordValidation = validatePassword(formData.password, isRTL);
    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.errors;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const sendOTPToWebhook = async (phone: string, name: string, email: string, method: 'whatsapp' | 'email'): Promise<string> => {
    // Generate 6-digit OTP
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    // Use the webhook URL from selected country
    const webhookUrl = selectedCountry.webhookUrl;
    const payload = {
      phone: getFullPhoneNumber(),
      name,
      email,
      otp,
      method,
      country: selectedCountry.code,
      timestamp: new Date().toISOString()
    };
    try {
      await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      // Try with no-cors mode
      await fetch(webhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    }
    console.log(`OTP sent to ${webhookUrl}:`, {
      ...payload,
      otp: '[HIDDEN]'
    });
    return otp;
  };
  const handleInitialSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate form
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    try {
      const otp = await sendOTPToWebhook(getFullPhoneNumber(), formData.name, formData.email, otpMethod);
      setGeneratedOTP(otp);
      setStep(2);
    } catch (error) {
      console.error(error);
      setErrors({
        general: t('auth.otpSendFailed')
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleOTPComplete = async (code: string) => {
    setIsLoading(true);
    try {
      // Verify OTP
      if (code === generatedOTP) {
        // Hash password before saving
        const hashedPassword = await hashPassword(formData.password);
        await register({
          ...formData,
          phone: getFullPhoneNumber(),
          password: hashedPassword,
          country: selectedCountry.code,
          brandName: formData.brandName
        });
        // Save country preference
        localStorage.setItem('operines_country', selectedCountry.code);
        setStep(3);
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        setErrors({
          general: t('auth.otpInvalid')
        });
      }
    } catch (error) {
      console.error(error);
      setErrors({
        general: t('auth.verificationError')
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleResendOTP = async () => {
    try {
      const otp = await sendOTPToWebhook(getFullPhoneNumber(), formData.name, formData.email, otpMethod);
      setGeneratedOTP(otp);
    } catch (error) {
      console.error(error);
    }
  };
  const handleSwitchMethod = async (newMethod: 'whatsapp' | 'email') => {
    try {
      setOtpMethod(newMethod);
      const otp = await sendOTPToWebhook(getFullPhoneNumber(), formData.name, formData.email, newMethod);
      setGeneratedOTP(otp);
    } catch (error) {
      console.error(error);
    }
  };
  const getPageTitle = () => {
    if (step === 1) return t('auth.register');
    if (step === 2) {
      return otpMethod === 'email' ? t('auth.confirmEmail') : t('auth.confirmPhone');
    }
    return t('auth.registerSuccess');
  };
  const getStrengthColor = () => {
    switch (passwordStrength) {
      case 'strong':
        return 'bg-green-500';
      case 'medium':
        return 'bg-yellow-500';
      default:
        return 'bg-red-500';
    }
  };
  const getStrengthText = () => {
    switch (passwordStrength) {
      case 'strong':
        return t('password.strong');
      case 'medium':
        return t('password.medium');
      default:
        return t('password.weak');
    }
  };
  return <div className="min-h-screen bg-white flex" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-2788">
      <SEO title={isRTL ? 'إنشاء حساب - تجربة مجانية 3 أيام' : 'Register - 3 Days Free Trial'} description={isRTL ? 'أنشئ حسابك في Operines واستمتع بتجربة مجانية 3 أيام. ابدأ أتمتة واتساب بالذكاء الاصطناعي فوراً بدون بطاقة ائتمان.' : 'Create your Operines account and enjoy a 3-day free trial. Start WhatsApp AI automation instantly with no credit card required.'} canonicalPath="/register" language={language as 'ar' | 'en'} data-id="element-2789" />

      {/* LEFT SIDE - FORM */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24 bg-white relative z-10" data-id="element-2790">
        <div className="w-full max-w-md mx-auto lg:mx-0 lg:max-w-lg py-8 lg:py-12" data-id="element-2791">
          {/* Logo */}
          <Link to="/" className="inline-block mb-8 lg:mb-12" data-id="element-2792">
            <img src="https://cdn.magicpatterns.com/uploads/8sRwXksp5brxBQY4QcgZ5M/U062aU0635U0645U064aU0645_U0628U062fU0648U0646_U0639U0646U0648U0627U0646_(37).png" alt="Operines" className="h-8 sm:h-10 w-auto" data-id="element-2793" />
          </Link>

          {/* Heading */}
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 mb-4" data-id="element-2794">
            {getPageTitle()}
          </h2>

          {step === 1 && <div className="mb-8" data-id="element-2795">
              <p className="text-base text-gray-600" data-id="element-2796">
                {isRTL ? 'أرسل أول رسالة في دقائق. هل لديك حساب بالفعل؟' : 'Send your first message in minutes. Already have an account?'}{' '}
                <Link to="/login" className="font-semibold text-[#4f1964] hover:text-[#3d1350] transition-colors underline decoration-2 decoration-transparent hover:decoration-[#4f1964]" data-id="element-2797">
                  {t('auth.login')}
                </Link>
              </p>
            </div>}

          {/* Form Container */}
          <div className="bg-white shadow-xl lg:shadow-none rounded-2xl lg:rounded-none p-6 lg:p-0 border lg:border-0 border-gray-100" data-id="element-2798">
            {errors.general && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3" data-id="element-2799">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-2800" />
                <p className="text-sm text-red-600 font-medium" data-id="element-2801">
                  {errors.general}
                </p>
              </motion.div>}

            <AnimatePresence mode="wait" data-id="element-2802">
              {step === 1 && <motion.form key="step1" initial={{
              opacity: 0,
              x: isRTL ? -20 : 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: isRTL ? 20 : -20
            }} className="space-y-4 sm:space-y-5" onSubmit={handleInitialSubmit} data-id="element-2803">
                  <Input label={t('auth.fullName')} name="name" value={formData.name} onChange={handleInputChange} required isRTL={isRTL} data-id="element-2804" />

                  <div data-id="element-2805">
                    <Input label={t('auth.email')} type="email" name="email" value={formData.email} onChange={handleInputChange} required isRTL={isRTL} error={errors.email} data-id="element-2806" />
                    {errors.email && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1.5" data-id="element-2807">
                        <AlertCircle className="w-4 h-4" data-id="element-2808" />
                        {errors.email}
                      </p>}
                  </div>

                  {/* Phone with Country Selector */}
                  <div data-id="element-2809">
                    <label className={`block text-sm font-medium text-gray-700 mb-1.5 ${isRTL ? 'text-right' : 'text-left'}`} data-id="element-2810">
                      {t('auth.phone')}
                    </label>
                    <div className="flex gap-2" data-id="element-2811">
                      {/* Country Selector */}
                      <div className="relative" ref={countryRef} data-id="element-2812">
                        <button type="button" onClick={() => setIsCountryOpen(!isCountryOpen)} className="flex items-center gap-1.5 px-3 py-2.5 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors min-w-[100px] sm:min-w-[120px]" data-id="element-2813">
                          <span className="text-xl sm:text-2xl" data-id="element-2814">
                            {selectedCountry.flag}
                          </span>
                          <span className="text-sm text-gray-600 font-medium" data-id="element-2815">
                            {selectedCountry.dialCode}
                          </span>
                          <ChevronDown size={14} className={`text-gray-400 transition-transform ml-auto ${isCountryOpen ? 'rotate-180' : ''}`} data-id="element-2816" />
                        </button>

                        <AnimatePresence data-id="element-2817">
                          {isCountryOpen && <motion.div initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -10
                      }} className={`absolute top-full mt-2 ${isRTL ? 'right-0' : 'left-0'} bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 min-w-[200px]`} data-id="element-2818">
                              {countries.map(country => <button key={country.code} type="button" onClick={() => {
                          setSelectedCountry(country);
                          setIsCountryOpen(false);
                          setFormData(prev => ({
                            ...prev,
                            phone: ''
                          }));
                        }} className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${selectedCountry.code === country.code ? 'bg-purple-50 text-[#4f1964]' : 'text-gray-700'}`} data-id="element-2819">
                                  <span className="text-xl" data-id="element-2820">
                                    {country.flag}
                                  </span>
                                  <span className="font-medium" data-id="element-2821">
                                    {isRTL ? country.nameAr : country.name}
                                  </span>
                                  <span className={`text-gray-400 text-xs ${isRTL ? 'mr-auto' : 'ml-auto'}`} data-id="element-2822">
                                    {country.dialCode}
                                  </span>
                                </button>)}
                            </motion.div>}
                        </AnimatePresence>
                      </div>

                      {/* Phone Input */}
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder={selectedCountry.placeholder} required className={`flex-1 px-4 py-2.5 sm:py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4f1964] focus:border-transparent transition-all text-sm sm:text-base ${errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50/50'}`} dir="ltr" data-id="element-2823" />
                    </div>
                    {errors.phone && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1.5" data-id="element-2824">
                        <AlertCircle className="w-4 h-4" data-id="element-2825" />
                        {errors.phone}
                      </p>}
                  </div>

                  <div data-id="element-2826">
                    <div className="relative" data-id="element-2827">
                      <Input label={t('auth.password')} type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleInputChange} required isRTL={isRTL} data-id="element-2828" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-9 text-gray-400 hover:text-gray-600 transition-colors`} data-id="element-2829">
                        {showPassword ? <EyeOff className="w-5 h-5" data-id="element-2830" /> : <Eye className="w-5 h-5" data-id="element-2831" />}
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    {formData.password && <div className="mt-3 p-3 bg-gray-50 rounded-lg" data-id="element-2832">
                        <div className="flex items-center gap-2 mb-2" data-id="element-2833">
                          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-2834">
                            <motion.div initial={{
                        width: 0
                      }} animate={{
                        width: passwordStrength === 'strong' ? '100%' : passwordStrength === 'medium' ? '66%' : '33%'
                      }} className={`h-full ${getStrengthColor()}`} data-id="element-2835" />
                          </div>
                          <span className={`text-xs font-bold ${passwordStrength === 'strong' ? 'text-green-600' : passwordStrength === 'medium' ? 'text-yellow-600' : 'text-red-600'}`} data-id="element-2836">
                            {getStrengthText()}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-id="element-2837">
                          {[{
                      check: formData.password.length >= 8,
                      text: t('password.minLength')
                    }, {
                      check: /[A-Z]/.test(formData.password),
                      text: t('password.uppercase')
                    }, {
                      check: /[a-z]/.test(formData.password),
                      text: t('password.lowercase')
                    }, {
                      check: /[0-9]/.test(formData.password),
                      text: t('password.number')
                    }, {
                      check: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password),
                      text: t('password.special')
                    }].map((req, i) => <div key={i} className="flex items-center gap-1.5" data-id="element-2838">
                              {req.check ? <Check className="w-3 h-3 text-green-500" data-id="element-2839" /> : <div className="w-3 h-3 rounded-full border border-gray-300" data-id="element-2840" />}
                              <span className={`text-xs ${req.check ? 'text-green-700 font-medium' : 'text-gray-500'}`} data-id="element-2841">
                                {req.text}
                              </span>
                            </div>)}
                        </div>
                      </div>}
                  </div>

                  {/* OTP Method Selection */}
                  <div className="space-y-3 pt-2" data-id="element-2842">
                    <label className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`} data-id="element-2843">
                      {t('auth.verificationMethod')}
                    </label>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-2844">
                      <button type="button" onClick={() => setOtpMethod('whatsapp')} className={`relative flex flex-col items-center justify-center gap-2 px-4 py-3 sm:py-4 rounded-xl border-2 transition-all ${otpMethod === 'whatsapp' ? 'border-[#4f1964] bg-purple-50 text-[#4f1964] shadow-sm' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2845">
                        {otpMethod === 'whatsapp' && <div className="absolute top-2 right-2 w-2 h-2 bg-[#4f1964] rounded-full" data-id="element-2846" />}
                        <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-2847" />
                        <span className="font-bold text-xs sm:text-sm" data-id="element-2848">
                          {t('auth.whatsapp')}
                        </span>
                      </button>
                      <button type="button" onClick={() => setOtpMethod('email')} className={`relative flex flex-col items-center justify-center gap-2 px-4 py-3 sm:py-4 rounded-xl border-2 transition-all ${otpMethod === 'email' ? 'border-[#4f1964] bg-purple-50 text-[#4f1964] shadow-sm' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2849">
                        {otpMethod === 'email' && <div className="absolute top-2 right-2 w-2 h-2 bg-[#4f1964] rounded-full" data-id="element-2850" />}
                        <Mail className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-2851" />
                        <span className="font-bold text-xs sm:text-sm" data-id="element-2852">
                          {t('auth.emailMethod')}
                        </span>
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 text-center bg-gray-50 py-2 rounded-lg" data-id="element-2853">
                      {otpMethod === 'whatsapp' ? t('auth.otpViaWhatsapp') : t('auth.otpViaEmail')}
                    </p>
                  </div>

                  <div className="pt-2" data-id="element-2854">
                    <Button type="submit" fullWidth isLoading={isLoading} size="lg" disabled={!validatePassword(formData.password, isRTL).isValid} className="bg-[#4f1964] hover:bg-[#3d1350] text-white shadow-lg shadow-purple-200 py-3 sm:py-4 text-base" data-id="element-2855">
                      {t('common.continue')}
                      {isRTL ? <ArrowLeft className="mr-2 w-5 h-5" data-id="element-2856" /> : <ArrowRight className="ml-2 w-5 h-5" data-id="element-2857" />}
                    </Button>
                  </div>
                </motion.form>}

              {step === 2 && <motion.div key="step2" initial={{
              opacity: 0,
              x: isRTL ? -20 : 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: isRTL ? 20 : -20
            }} data-id="element-2858">
                  <div className="mb-6 text-center" data-id="element-2859">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${otpMethod === 'whatsapp' ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`} data-id="element-2860">
                      {otpMethod === 'whatsapp' ? <MessageSquare className="w-4 h-4 text-green-600" data-id="element-2861" /> : <Mail className="w-4 h-4 text-blue-600" data-id="element-2862" />}
                      <span className={`text-sm font-bold ${otpMethod === 'whatsapp' ? 'text-green-700' : 'text-blue-700'}`} data-id="element-2863">
                        {otpMethod === 'whatsapp' ? t('auth.sentViaWhatsapp') : t('auth.sentViaEmail')}
                      </span>
                    </div>
                  </div>
                  <OTPVerification phone={getFullPhoneNumber()} email={formData.email} method={otpMethod} onComplete={handleOTPComplete} onResend={handleResendOTP} onSwitchMethod={handleSwitchMethod} data-id="element-2864" />
                </motion.div>}

              {step === 3 && <motion.div key="step3" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="text-center py-12" data-id="element-2865">
                  <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner" data-id="element-2866">
                    <CheckCircle className="w-12 h-12 text-green-600" data-id="element-2867" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2868">
                    {t('auth.registerSuccess')}
                  </h3>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-full mb-6" data-id="element-2869">
                    <span className="text-green-700 font-bold text-sm" data-id="element-2870">
                      {t('auth.freeTrialActivated')}
                    </span>
                  </div>
                  <p className="text-gray-500 animate-pulse" data-id="element-2871">
                    {t('auth.redirecting')}
                  </p>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - DECORATIVE (Hidden on mobile) */}
      <div className="hidden lg:flex flex-1 relative bg-white overflow-hidden items-center justify-center p-12 border-l border-gray-100" data-id="element-2872">
        {/* Content Container */}
        <div className="relative z-10 max-w-md w-full" data-id="element-2873">
          {/* Trust Card */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} className="bg-white border-2 border-gray-200 rounded-3xl p-8 shadow-sm" data-id="element-2874">
            <div className="flex items-center gap-4 mb-6" data-id="element-2875">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center" data-id="element-2876">
                <img src="https://cdn.magicpatterns.com/uploads/faWT17kE43WdqrZMBmZAS3/Operines-Icon-color1_(2).png" alt="Icon" className="w-8 h-8 object-contain" data-id="element-2877" />
              </div>
              <div data-id="element-2878">
                <h3 className="font-bold text-lg text-gray-900" data-id="element-2879">
                  {isRTL ? 'محبوب من المستخدمين' : 'Loved by users everywhere'}
                </h3>
                <div className="flex gap-1 text-yellow-400 mt-1" data-id="element-2880">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-current" data-id="element-2881" />)}
                </div>
              </div>
            </div>

            <div className="space-y-3" data-id="element-2882">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl" data-id="element-2883">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600" data-id="element-2884">
                  <Users className="w-5 h-5" data-id="element-2885" />
                </div>
                <div data-id="element-2886">
                  <div className="text-2xl font-bold text-gray-900" data-id="element-2887">+500</div>
                  <div className="text-sm text-gray-500" data-id="element-2888">
                    {isRTL ? 'عميل نشط' : 'Active Clients'}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl" data-id="element-2889">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600" data-id="element-2890">
                  <TrendingUp className="w-5 h-5" data-id="element-2891" />
                </div>
                <div data-id="element-2892">
                  <div className="text-2xl font-bold text-gray-900" data-id="element-2893">99.9%</div>
                  <div className="text-sm text-gray-500" data-id="element-2894">
                    {isRTL ? 'وقت التشغيل' : 'Uptime'}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl" data-id="element-2895">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-[#4f1964]" data-id="element-2896">
                  <MessageSquare className="w-5 h-5" data-id="element-2897" />
                </div>
                <div data-id="element-2898">
                  <div className="text-2xl font-bold text-gray-900" data-id="element-2899">+10K</div>
                  <div className="text-sm text-gray-500" data-id="element-2900">
                    {isRTL ? 'رسالة يومياً' : 'Messages Daily'}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t border-gray-100 text-center" data-id="element-2901">
              <p className="text-sm text-gray-500 italic" data-id="element-2902">
                "
                {isRTL ? 'أفضل منصة لأتمتة واتساب في الشرق الأوسط' : 'The best WhatsApp automation platform in the Middle East'}
                "
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}